import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, del, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const TYPES = ["image/png", "image/jpeg"];
const MAX = 2 * 1024 * 1024;

function useLogoUrl(stamp) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!stamp) { setUrl(null); return undefined; }
    let objectUrl;
    let live = true;
    api.get("/company/logo", { responseType: "blob", params: { v: stamp } })
      .then((r) => { if (live) { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); } })
      .catch(() => live && setUrl(null));
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [stamp]);
  return url;
}

export const LogoUpload = () => {
  const { profile, refreshProfile } = useAuth();
  const stamp = profile.company?.logo_updated_at;
  const url = useLogoUrl(stamp);
  const input = useRef(null);
  const [busy, setBusy] = useState(null);

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
      await refreshProfile();
      toast.success(kind === "upload" ? "Logo saved. It will appear on PDF run exports." : "Logo removed");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) { toast.error("Logo must be a PNG or JPG image"); return; }
    if (file.size > MAX) { toast.error("Logo is too large (max 2 MB)"); return; }
    const body = new FormData();
    body.append("file", file);
    run("upload", () => api.post("/company/logo", body));
  };

  return (
    <div className="space-y-2" data-testid="logo-upload">
      <p className="text-sm font-medium text-slate-900">Company logo</p>
      <p className="text-xs text-slate-500">Shown top-left on PDF run exports. PNG or JPG, up to 2 MB.</p>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-40 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2">
          {url ? <img src={url} alt="Company logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
            : <span className="text-xs text-slate-500" data-testid="logo-empty">No logo yet</span>}
        </div>
        <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="logo-file-input" />
        <Button type="button" variant="outline" disabled={!!busy} onClick={() => input.current?.click()} data-testid="logo-upload-button">
          {busy === "upload" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1 h-4 w-4" />} {stamp ? "Replace logo" : "Upload logo"}
        </Button>
        {stamp && (
          <Button type="button" variant="ghost" disabled={!!busy} onClick={() => run("remove", () => del("/company/logo"))} className="text-rose-700" data-testid="logo-remove-button">
            {busy === "remove" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />} Remove
          </Button>)}
      </div>
    </div>
  );
};
